import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { useDeleteContractMutation, useGetContractsQuery, useGetCustomerQuery } from "../../../Api/attoDeskApi";
import { IContract, ICustomer } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";
import { fmtCustomerName } from "../../../Core/utils/nameUtils";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";


const ContractList: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage } = useNotifier();

  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 300);
  const [deleteId, setDeleteId]     = useState<number | null>(null);

  const { data, refetch, isFetching } = useGetContractsQuery({ searchText: debouncedSearch });
  const [deleteContract, { isLoading: isDeleting }] = useDeleteContractMutation();
  const { data: customerData } = useGetCustomerQuery({ searchText: "", id: "", quoteWorkOrderCustomerId: "" });

  const contracts = useMemo(() => (data?.data as IContract[]) ?? [], [data]);
  const customers = useMemo(() => (customerData?.data as ICustomer[]) ?? [], [customerData]);

  const clientName = (clientId: number) => {
    const c = customers.find((x) => x.id === clientId);
    if (!c) return `#${clientId}`;
    return fmtCustomerName(c, `#${clientId}`);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteContract(String(deleteId)).unwrap();
      showMessage("Contract deleted", "success");
      refetch();
    } catch {
      showMessage("Failed to delete contract", "error");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AssignmentOutlinedIcon sx={{ color: "#2f3891", fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color="#0f172a">Recurring Contracts</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage billing templates for repeat clients
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/contract/create")}
          sx={{ textTransform: "none", borderRadius: 2, bgcolor: "#2f3891", "&:hover": { bgcolor: "#2f3891" } }}
        >
          New Contract
        </Button>
      </Box>

      {/* ── Search + table ── */}
      <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #e2e8f0" }}>
          <TextField
            size="small"
            placeholder="Search by contract name…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><CiSearch size={18} /></InputAdornment>,
            }}
            sx={{ width: 280 }}
          />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Contract Name</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Date Range</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#64748b" }} align="center">Items</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#64748b", width: 100 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : contracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <AssignmentOutlinedIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                    <Typography color="text.secondary">No contracts found</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Create a contract to enable auto-fill on invoice creation
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contracts.map((c) => {
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontWeight: 600, color: "#0f172a" }}>{c.contract_name}</TableCell>
                      <TableCell>{clientName(c.client_id)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {c.start_date} → {c.end_date}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={c.items?.length ?? 0}
                          size="small"
                          sx={{ bgcolor: "#ede9fe", color: "#2f3891", fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/contract/edit/${c.id}`)}
                            sx={{ color: "#2f3891" }}
                          >
                            <EditSharpIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteId(c.id)}
                            sx={{ color: "#ef4444" }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Delete confirm ── */}
      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Contract?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This contract will be removed. Existing invoices will not be affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={isDeleting}
            sx={{ textTransform: "none", bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" } }}
          >
            {isDeleting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractList;
